import { Component, OnInit } from '@angular/core';
import { HeaderComponent } from '../common/header/header.component';
import { SidebarComponent } from '../common/slidebar/sidebar.components';
import { VirtualNumberService } from '../../services/virtual-number/virtual-number.service';
import { SearchServiceStatusService } from '../../services/search-service-status/search-service-status.service';
import { ToastrService } from 'ngx-toastr';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { retry } from 'rxjs/operators';
import { RestrictedAccessWarningDirective } from '../../directives/restricted-access-warning.directive';
import { UserAccountService } from '../../services/user-account/user-account.service';
import { Router } from '@angular/router';
import { CreditsLabelPipe } from '../../pipes/credits-label.pipe';

@Component({
  selector: 'app-virtual-number',
  standalone: true,
  imports: [CommonModule, FormsModule, HeaderComponent, SidebarComponent, RestrictedAccessWarningDirective, CreditsLabelPipe],
  templateUrl: './virtual-number.component.html',
  styleUrls: ['./virtual-number.component.css']
})
export class VirtualNumberComponent implements OnInit {
  availableCredits: number | null = null;
  creditsLoading = true;
  totalCredits: number = 0;
  countries: any[] = [];
  filteredCountries: any[] = [];
  selectedCountry: any;
  searchTextCountry: string = '';
  services: any[] = [];
  filteredServices: any[] = [];
  searchTextService: string = '';
  selectedService: any;
  purchasedNumber: any = null;
  expiryTime: any = null;
  timer: any = null;
  messages: any = [];
  maxRetry = 10;
  currentRetry = 0;
  messageTimer: any;


  // Loader states
  isLoadingCountries: boolean = false;
  isLoadingServices: boolean = false;
  isLoadingPurchase: boolean = false;
  isLoadingCancel: boolean = false;
  isLoadingMessages: boolean = false;
  isServiceDisabled: boolean = false;
  searchCreditCost: number | null = null;

  constructor(
    private vnService: VirtualNumberService,
    private toaster: ToastrService,
    private searchServiceStatusService: SearchServiceStatusService,
    private userAccountService: UserAccountService,
    private router: Router
  ) { }

  ngOnInit(): void {
    // Check if Virtual service is disabled
    this.searchServiceStatusService.searchServices$.subscribe((searchServices) => {
      if (searchServices) {
        this.isServiceDisabled = searchServices.virtual === false;
      }

      this.getAvailableCredits();
      if (!this.isServiceDisabled) {
        this.loadCountries();
        this.startMessagePolling();
      }
    });

    this.searchServiceStatusService.creditCosts$.subscribe((cc) => {
      this.searchCreditCost = this.searchServiceStatusService.resolveCreditCost(
        cc,
        'virtualNumberCreditCost',
      );
    });
  }

  getAvailableCredits() {
    this.creditsLoading = true;
    this.userAccountService.getUserProfile().subscribe({
      next: (response) => {
        const subscription = response?.data?.subscriptions?.[0] || {};
        this.availableCredits = this.userAccountService.pickAvailableCredits(response);
        this.creditsLoading = false;
        this.totalCredits = subscription?.total_credits;
      },
      error: (error) => {
        console.error(error);
      
        this.availableCredits = 0;
        this.creditsLoading = false;
      },
      complete: () => { },
    });
  }

  onBuyCreditsButtonClick() {
    this.router.navigate(['/plan-pricing']);
  }

  startMessagePolling() {
    // Clear any existing timer first
    if (this.messageTimer) {
      clearInterval(this.messageTimer);
      this.messageTimer = null;
    }

    this.messageTimer = setInterval(() => {
      if (this.purchasedNumber != null) {
        this.vnService.refreshStatus(this.purchasedNumber.virtual_number_id).subscribe((data) => { });
        this.vnService.fetchMessages(this.purchasedNumber.virtual_number_id).subscribe((data: any) => {
          this.messages = data.data.messages;
          // Stop loading messages and polling once we receive at least one message
          if (this.messages.length > 0) {
            this.isLoadingMessages = false;
            this.stopMessagePolling();
          }
        }, (error) => {
          // Handle error silently or log it
        });
      }
    }, 5000);
  }

  stopMessagePolling() {
    if (this.messageTimer) {
      clearInterval(this.messageTimer);
      this.messageTimer = null;
    }
  }

  loadCountries() {
    if (this.isLoadingCountries) return; // Prevent multiple calls

    this.isLoadingCountries = true;
    // Reset lists before loading to avoid duplicate rows on re-fetch/re-subscribe.
    this.countries = [];
    this.filteredCountries = [];
    this.vnService.getCountryList().subscribe(
      (data) => {
        Object.values(data.data).forEach((country: any) => {
          const extra: any = this.vnService.countries.find((x: any) => x.name === country.eng);
          if (country.visible == 1) {
            this.countries.push({
              id: country.id,
              name: country.eng,
              iso: extra?.iso || '',
              code: extra?.dial || ''
            });
          }
        });
        // De-duplicate by backend country id in case upstream response contains repeats.
        const uniqueCountries = Array.from(
          new Map(this.countries.map((country) => [country.id, country])).values()
        );

        this.countries = uniqueCountries.sort((a, b) => a.name.localeCompare(b.name));
        this.filteredCountries = [...this.countries];
        if (this.countries.length > 0) {
          this.updateCountry(this.countries[0]);
        }
        this.isLoadingCountries = false;
      },
      (error) => {
        this.isLoadingCountries = false;
        this.toaster.error(error.error.message_key || "Unable to fetch Country List");
      }
    );
  }

  startTimer() {
    clearInterval(this.timer);
    this.timer = setInterval(() => {
      const pastTime = new Date(this.purchasedNumber.expires_at).getTime();
      const nowTime = Date.now();
      const diffInSeconds = Math.floor((pastTime - nowTime) / 1000);
      if (diffInSeconds < 0) {
        this.purchasedNumber = null;
        this.messages = [];
        this.toaster.error("Virtual Number Expired");
        clearInterval(this.timer);
        this.stopMessagePolling();
      }
      const mins = Math.floor(diffInSeconds / 60);
      const secs = diffInSeconds % 60;
      this.expiryTime = String(mins).padStart(2, "0") + ":" + String(secs).padStart(2, "0");
    }, 1000)
  }

  stopTimer() {
    clearInterval(this.timer);
  }

  // Country search
  filterCountries() {
    const text = this.searchTextCountry.toLowerCase();
    this.filteredCountries = this.countries.filter(country =>
      country.name.toLowerCase().includes(text) ||
      country.code.toString().includes(text)
    );
  }

  // Site search
  filterServices() {
    const text = this.searchTextService.toLowerCase();
    this.filteredServices = this.services.filter(service =>
      service.name.toLowerCase().includes(text)
    );
  }

  updateCountry(country: any) {
    if (this.isLoadingServices) return; // Prevent multiple calls

    this.selectedCountry = country;
    this.isLoadingServices = true;
    this.services = [];
    this.filteredServices = [];

    this.vnService.getServiceList(this.selectedCountry.id).subscribe((data) => {
      this.services = data.data.services;
      this.services = this.services.sort((a, b) => {
        return a.name.localeCompare(b.name);
      });
      this.filteredServices = [...this.services];
      this.isLoadingServices = false;
    }, (error) => {
      this.isLoadingServices = false;
      this.toaster.error(error.error.message_key || "Unable to fetch Service List");
    });
  }

  onImgError(event: any, service: any) {
    const initials = service.name
      .split(' ')
      .map((n: string) => n[0])
      .join('')
      .toUpperCase();

    // Generate random hex color
    const randomColor = Math.floor(Math.random() * 16777215).toString(16).padStart(6, '0');

    event.target.src = `https://placehold.co/24x24/${randomColor}/FFFFFF/png?text=${initials}`;
  }

  purchaseNumber(service: any) {
    if (this.isLoadingPurchase) return;

    this.messages = [];
    this.selectedService = service;
    this.isLoadingPurchase = true;
    this.isLoadingMessages = true; // Start loading messages

    this.currentRetry = 0;

    const attempt = () => {
      this.vnService
        .purchaseNumber(this.selectedCountry.id, this.selectedService.code)
        .subscribe({
          next: (data: any) => {
            this.purchasedNumber = {
              ...data.data,
              country: this.selectedCountry
            };

            this.toaster.success("Virtual Number Generated Successfully !");
            this.startTimer();
            this.startMessagePolling(); // Start polling for messages

            this.isLoadingPurchase = false;
            // Keep isLoadingMessages true until we receive messages
          },

          error: (error) => {
            this.currentRetry++;

            if (this.currentRetry <= this.maxRetry) {
              attempt(); // retry again
            } else {
              this.isLoadingPurchase = false;
              this.isLoadingMessages = false;
              this.toaster.error(error.error?.message_key || "Unable to purchase Number");
            }
          }
        });
    };

    attempt();
  }

  cancelNumber() {
    if (this.isLoadingCancel) return; // Prevent multiple calls

    this.isLoadingCancel = true;

    this.vnService.cancelNumber(this.purchasedNumber.virtual_number_id).subscribe((data: any) => {
      this.toaster.success(data.message_key);
      this.purchasedNumber = null;
      this.messages = [];
      this.stopTimer();
      this.stopMessagePolling();
      this.isLoadingCancel = false;
      this.isLoadingMessages = false;
    }, (error) => {
      this.isLoadingCancel = false;
      this.toaster.error(error.error.message_key || "Unable to Cancel Purchased Number");
    });
  }

  copyPhoneNumber() {
    if (!this.purchasedNumber || !this.purchasedNumber.phone_number) {
      this.toaster.error("No phone number to copy");
      return;
    }

    const phoneNumber = this.purchasedNumber.phone_number;

    // Modern clipboard API
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(phoneNumber).then(() => {
        this.toaster.success("Phone number copied to clipboard!");
      }).catch(err => {
        console.error('Failed to copy: ', err);
        this.fallbackCopy(phoneNumber);
      });
    } else {
      // Fallback for older browsers
      this.fallbackCopy(phoneNumber);
    }
  }

  private fallbackCopy(text: string) {
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.left = '-999999px';
    textArea.style.top = '-999999px';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();

    try {
      const successful = document.execCommand('copy');
      if (successful) {
        this.toaster.success("Phone number copied to clipboard!");
      } else {
        this.toaster.error("Failed to copy phone number");
      }
    } catch (err) {
      console.error('Fallback copy failed: ', err);
      this.toaster.error("Failed to copy phone number");
    }

    document.body.removeChild(textArea);
  }

  ngOnDestroy(): void {
    if (this.timer) {
      clearInterval(this.timer);
    }
    if (this.messageTimer) {
      clearInterval(this.messageTimer);
    }
  }
}